import { create } from 'zustand'
import { aiAPI, messengerAPI } from '../services/api'
// ★ v242.156.0 — the SHAPE of a stored turn lives in its own dependency-free module
//   so a gate can execute it; see agentHistory.js for why that matters.
import { agentMessagesFromStored } from './agentHistory'
import { newAgentMessageId, resolvePrepIndex } from './agentPrepRef'
import { threadMessages, threadBubble, seenServerIds, BUBBLE_ROLE_PATIENT } from '../utils/caseThread'
import { clinicianFromText, clinicianName } from '../utils/clinicianName.js'

// ★★★ v242.125.0 · OPENER-RESEED-BREAKER — a browser tab is never worth a greeting.
//
// getConversation() re-seeds a still-unsent thread when its opener changes, and it
// does so with a set() DURING RENDER. That is only survivable while exactly one
// mounted agent claims the opener for a given caseId. v242.122.0 made a second one
// possible (the global sheet on Deliver vs CaseHome's inline agent), and because
// <Routes> lives inside <AnimatePresence> both are mounted together during a route
// transition — so the two re-seeded each other, synchronously, forever, and locked
// the tab. DELIVER-AGENT-OPENER-2 removes that particular disagreement at source.
//
// THIS IS THE BACKSTOP, NOT THE FIX. Any future screen that hands down an opener can
// recreate the argument, and the failure mode is a hard browser lock with a minified
// stack — the most expensive possible way to learn it. A thread that has re-seeded
// this many times in this short a window is not tracking a screen, it is oscillating:
// stop re-seeding it and let the last opener stand. The thread stays usable; only the
// greeting is stale, which is the defect v242.122.0 fixed, not one that costs a session.

/**
 * s120 · the case thread's order: the opener (the screen's line the thread was seeded with) is pinned FIRST,
 * everything else follows by time. A stored thread from before the pin is recognised by its opener text.
 * Pure; exported for the gate.
 */
export function orderThread(messages, openerText) {
  const list = Array.isArray(messages) ? messages.slice() : []
  const text = typeof openerText === 'string' ? openerText.trim() : ''
  let at = list.findIndex((m) => m && m._isOpener)
  if (at < 0 && text) at = list.findIndex((m) => m && m.role === 'assistant' && typeof m.content === 'string' && m.content.trim() === text)
  const opener = at >= 0 ? list.splice(at, 1)[0] : null
  list.sort((a, b) => ((a && a.timestamp) || 0) - ((b && b.timestamp) || 0))
  return opener ? [opener, ...list] : list
}

const RESEED_LIMIT = 6
const RESEED_WINDOW_MS = 2000
const _reseeds = new Map() // caseId -> number[] of timestamps

function reseedAllowed(caseId) {
  const now = Date.now()
  const hits = (_reseeds.get(caseId) || []).filter((t) => now - t < RESEED_WINDOW_MS)
  if (hits.length >= RESEED_LIMIT) {
    _reseeds.set(caseId, hits)
    if (hits.length === RESEED_LIMIT) {
      console.warn('[useAgentStore] opener re-seed loop suppressed for case', caseId,
        '- two mounted agents are claiming different openers for the same thread')
      hits.push(now) // only warn once per window
    }
    return false
  }
  hits.push(now)
  _reseeds.set(caseId, hits)
  return true
}

// Agent greeting translations (store can't use React hooks, so inline lookup)
// v169.4.0: Greetings accept optional firstName for personalization
const AGENT_GREETINGS = {
  en: {
    welcome: (name) => name
      ? `Hi ${name} — welcome to Orella. I can help you understand how the platform works, answer general health questions, or help you start a new case. What's on your mind?`
      : "Welcome to Orella. I can help you understand how the platform works, answer general health questions, or help you start a new case. What's on your mind?",
    // v200.0: Triage greeting — used when patient types a symptom on the home screen.
    // Acknowledges they're describing a concern, doesn't repeat generic intro.
    triage: (name) => name
      ? `Hi ${name} — tell me what's going on and I'll help you get started with the right specialist.`
      : "Tell me what's going on and I'll help you get started with the right specialist.",
    profileIncomplete: (missing, name) => `${name ? `${name}, let's` : "Let's"} make sure your health profile is complete — I can help you add ${missing.slice(0, 2).join(' and ')} right here in our conversation. Just tell me what you'd like to add.`,
    profileComplete: (name) => `${name ? `${name}, your` : "Your"} profile is looking complete. If anything has changed — new medications, a recent surgery, or an allergy you forgot to mention — just let me know and I'll update it.`,
    describePhase: (topic, name) => `${name ? `${name}, I'm` : "I'm"} here while you work through describing ${topic}. If you have questions about the process or want to know why we're asking something specific, just ask.`,
    detectPhase: (name) => `${name ? `${name}, your` : "Your"} description is looking good. You can add photos in Detect if they'd help your doctor, or move on to sharing your case. What would you like to know?`,
    shareExternal: "You're preparing for your doctor's visit. I can explain how your Case Report works, help you think about questions to ask your doctor, or walk you through the next steps.",
    shareOrella: "Ready to share your case with a doctor. I can explain how the share code works, help you choose between an Orella doctor or preparing for your own doctor, or answer questions about the process.",
    diagnoseExternal: "I can help you prepare for your appointment, explain what to bring, or guide you through uploading your doctor's notes when you're back.",
    diagnoseDrOrella: (name) => `${name} is reviewing your case. While you wait, I can explain what typically happens next, what your doctor is looking at, or answer questions about the process.`,
    diagnoseWaiting: "Your doctor is reviewing your case. I can explain what happens during the review, help you prepare questions, or walk you through what to expect next.",
    diagnoseWithDoctor: (name) => `Your case is with ${name}. I can explain what happens during the review, what to expect, or answer questions while you wait.`,
    deliverPhase: "Your care plan is ready. I can help you understand your instructions, explain your medications, or walk you through any next steps your doctor ordered.",
    deliverResults: (name) => `${name ? `${name}, your` : "Your"} results are in. I can help explain your diagnosis, walk through your care plan, or answer questions about your treatment and next steps.`,
    deliverConsultComplete: "Your consultation is complete. I can help you understand your results, explain next steps, or answer questions about your care plan.",
    deliverComplete: "Your care is complete for this case. If you have questions about your instructions or need to start a new case, I'm here to help.",
    // v235.4.0 — post-visit states so the opener stops assuming the patient is still describing.
    followUpPhase: (name) => `${name ? `${name}, your` : "Your"} follow-up is underway. I can explain what your doctor is checking on, help you prepare an update on how you've been, or answer questions about your care.`,
    observationPhase: (name) => `${name ? `${name}, your` : "Your"} doctor is keeping an eye on this one. I can help you log how you're doing, explain what to watch for, or answer questions while you're monitored.`,
    awaitingReview: (name) => `${name ? `${name}, you're` : "You're"} checked in and your visit is in motion. I can explain what happens next, help you gather questions for your doctor, or walk you through what to expect.`,
    // v235.16.0 — temporal openers between "scheduled" and "checked in".
    appointmentImminent: (name) => `${name ? `${name}, your` : "Your"} appointment is coming up. When you're ready, tap Diagnose to check in — it only takes a moment. I can help you prep questions or know what to expect.`,
    appointmentConfirmed: (doctor, when, name) => {
      const who = doctor ? `with ${doctor}` : ''
      const dateBit = when ? ` for ${when}` : ''
      return `${name ? `${name}, your` : "Your"} appointment ${who} is confirmed${dateBit}. Want to get ready? I can help you think through what to ask, or add photos and documents in Detect before your visit.`.replace(/\s+/g, ' ').trim()
    },
    caseReviewing: (name) => `${name ? `${name}, your` : "Your"} case has been accepted and your doctor is getting it ready. I can help you prep questions, or add photos and documents in Detect while you wait.`,
    // v223.3.0 (F3): screen-purpose greetings — no specific case referenced.
    casesScreen: "Here are your cases. Tap any case to continue, or head to Home to start a new one. I can help you find one or explain what a status means.",
    visitsScreen: "Your upcoming and past visits are here. Need help preparing for an appointment, or have a question about one?",
    doctorsScreen: "Looking for the right doctor? I can help you understand specialties, what to expect, or how to share a case with a physician.",
    rateLimit: "I want to make sure you're getting the right answers. You've asked a lot of questions this hour — your doctor will be able to address them directly when you connect.",
  },
  es: {
    welcome: (name) => name
      ? `Hola ${name} — bienvenido a Orella. Puedo ayudarle a entender cómo funciona la plataforma, responder preguntas generales de salud o ayudarle a iniciar un nuevo caso. ¿En qué puedo ayudarle?`
      : "Bienvenido a Orella. Puedo ayudarle a entender cómo funciona la plataforma, responder preguntas generales de salud o ayudarle a iniciar un nuevo caso. ¿En qué puedo ayudarle?",
    // v242.126.0 — the ONE greeting that had no Spanish. A patient who typed a
    // symptom on the home screen in Spanish was answered in English, and nothing
    // could see it: this map is not a locale file, so check-i18n-keys never looks.
    triage: (name) => name
      ? `Hola ${name} — cuéntame qué está pasando y te ayudo a empezar con el especialista adecuado.`
      : "Cuéntame qué está pasando y te ayudo a empezar con el especialista adecuado.",
    profileIncomplete: (missing, name) => {
      const esMap = { 'allergies': 'alergias', 'medications': 'medicamentos', 'medical history': 'historial médico', 'family history': 'historial familiar' };
      const esMissing = missing.map(m => esMap[m] || m);
      return `${name ? `${name}, asegurémonos` : "Asegurémonos"} de que su perfil de salud esté completo — puedo ayudarle a agregar ${esMissing.slice(0, 2).join(' y ')} aquí en nuestra conversación. Dígame qué le gustaría agregar.`;
    },
    profileComplete: (name) => `${name ? `${name}, su` : "Su"} perfil se ve completo. Si algo ha cambiado — nuevos medicamentos, una cirugía reciente o una alergia que olvidó mencionar — solo avíseme y lo actualizaré.`,
    describePhase: (topic, name) => `${name ? `${name}, estoy` : "Estoy"} aquí mientras describe ${topic}. Si tiene preguntas sobre el proceso o quiere saber por qué preguntamos algo específico, solo pregunte.`,
    detectPhase: (name) => `${name ? `${name}, su` : "Su"} descripción se ve bien. Puede agregar fotos en Detectar si ayudarían a su doctor, o continuar para compartir su caso. ¿Qué le gustaría saber?`,
    shareExternal: "Se está preparando para la visita con su doctor. Puedo explicarle cómo funciona la Preparación de Visita, ayudarle a pensar en preguntas para su doctor o guiarle en los próximos pasos.",
    shareOrella: "Listo para compartir su caso con un doctor. Puedo explicarle cómo funciona el código para compartir, ayudarle a elegir entre un doctor de Orella o prepararse para su propio doctor.",
    diagnoseExternal: "Puedo ayudarle a prepararse para su cita, explicarle qué llevar o guiarle para subir las notas de su doctor cuando regrese.",
    diagnoseDrOrella: (name) => `${name} está revisando su caso. Mientras espera, puedo explicarle qué sucede típicamente después, qué está revisando su doctor o responder preguntas sobre el proceso.`,
    diagnoseWaiting: "Su doctor está revisando su caso. Puedo explicarle qué pasa durante la revisión, ayudarle a preparar preguntas o guiarle sobre qué esperar.",
    diagnoseWithDoctor: (name) => `Su caso está con ${name}. Puedo explicarle qué pasa durante la revisión, qué esperar o responder preguntas mientras espera.`,
    deliverPhase: "Su plan de atención está listo. Puedo ayudarle a entender sus instrucciones, explicarle sus medicamentos o guiarle en los próximos pasos que su doctor ordenó.",
    deliverResults: (name) => `${name ? `${name}, sus` : "Sus"} resultados están listos. Puedo ayudarle a explicar su diagnóstico, revisar su plan de atención o responder preguntas sobre su tratamiento y próximos pasos.`,
    deliverConsultComplete: "Su consulta está completa. Puedo ayudarle a entender sus resultados, explicar los próximos pasos o responder preguntas sobre su plan de atención.",
    deliverComplete: "Su atención está completa para este caso. Si tiene preguntas sobre sus instrucciones o necesita iniciar un nuevo caso, estoy aquí para ayudar.",
    // v235.4.0 — estados post-visita para que el saludo no asuma que aún describe.
    followUpPhase: (name) => `${name ? `${name}, su` : "Su"} seguimiento está en curso. Puedo explicarle qué está revisando su doctor, ayudarle a preparar una actualización sobre cómo se ha sentido o responder preguntas sobre su atención.`,
    observationPhase: (name) => `${name ? `${name}, su` : "Su"} doctor está monitoreando este caso. Puedo ayudarle a registrar cómo se siente, explicarle qué observar o responder preguntas mientras está en observación.`,
    awaitingReview: (name) => `${name ? `${name}, ya` : "Ya"} hizo su registro y su visita está en marcha. Puedo explicarle qué sigue, ayudarle a reunir preguntas para su doctor o guiarle sobre qué esperar.`,
    // v235.16.0 — saludos temporales entre "agendada" y "registrado".
    appointmentImminent: (name) => `${name ? `${name}, su` : "Su"} cita se acerca. Cuando esté listo, toque Diagnosticar para registrarse — solo toma un momento. Puedo ayudarle a preparar preguntas o saber qué esperar.`,
    appointmentConfirmed: (doctor, when, name) => {
      const who = doctor ? `con ${doctor}` : ''
      const dateBit = when ? ` para ${when}` : ''
      return `${name ? `${name}, su` : "Su"} cita ${who} está confirmada${dateBit}. ¿Quiere prepararse? Puedo ayudarle a pensar qué preguntar, o agregar fotos y documentos en Detectar antes de su visita.`.replace(/\s+/g, ' ').trim()
    },
    caseReviewing: (name) => `${name ? `${name}, su` : "Su"} caso ha sido aceptado y su doctor lo está preparando. Puedo ayudarle a preparar preguntas, o agregar fotos y documentos en Detectar mientras espera.`,
    // v223.3.0 (F3): saludos por pantalla — sin referenciar un caso específico.
    casesScreen: "Aquí están sus casos. Toque cualquier caso para continuar, o vaya a Inicio para comenzar uno nuevo. Puedo ayudarle a encontrar uno o explicar qué significa un estado.",
    visitsScreen: "Aquí están sus visitas próximas y pasadas. ¿Necesita ayuda para prepararse para una cita o tiene una pregunta sobre alguna?",
    doctorsScreen: "¿Busca el médico adecuado? Puedo ayudarle a entender las especialidades, qué esperar o cómo compartir un caso con un médico.",
    rateLimit: "Quiero asegurarme de que obtenga las respuestas correctas. Ha hecho muchas preguntas esta hora — su doctor podrá atenderlas directamente cuando se conecten.",
  },
}

// Bilingual suggestion buttons (Pattern C: localStorage lookup)
const AGENT_SUGGESTIONS = {
  en: {
    helpMeAdd: (section) => `Help me add my ${section}`,
    whyProfileMatters: 'Why does my profile matter?',
    whatDoctorSees: 'What info does my doctor see?',
    reviewAllergies: 'Review my allergies',
    updateMedications: 'Update my medications',
  },
  es: {
    helpMeAdd: (section) => {
      const esMap = { 'allergies': 'alergias', 'medications': 'medicamentos', 'medical history': 'historial médico', 'family history': 'historial familiar' };
      return `Ayúdame a agregar mis ${esMap[section] || section}`;
    },
    whyProfileMatters: '¿Por qué importa mi perfil?',
    whatDoctorSees: '¿Qué información ve mi doctor?',
    reviewAllergies: 'Revisar mis alergias',
    updateMedications: 'Actualizar mis medicamentos',
  },
}

const getAgentLang = () => {
  try { return localStorage.getItem('orella-language') || 'en' } catch { return 'en' }
}

// v234.4 — map a thrown/returned HTTP status to a failure kind the UI can speak to.
// 429 → rate-limited; 0 (network) / 408 (timeout) → offline; everything else → server.
const _classifyAgentStatus = (status) => {
  if (status === 429) return 'rate'
  if (status === 0 || status === 408) return 'offline'
  return 'server'
}

/**
 * Orella Agent Store v132.6.0
 * Manages per-case conversation state, profile actions, and rate limiting
 * Changes: per-case loading (AGT-3), profile-specific greeting (AGT-2)
 */
const useAgentStore = create((set, get) => ({
  conversations: {},
  loadingCaseId: null, // v132.6.0: per-case loading instead of global boolean
  error: null,
  _messageCounts: {},
  // v242.156.0 — which cases this ACCOUNT has already pulled the record for. Cleared
  // by resetForUser with everything else, so a switched account re-loads under its own
  // authorization rather than inheriting a decision made for someone else.
  _hydrated: {},

  // v242.40.0: wipe all in-memory agent conversations on account switch / logout.
  // Conversations are per-case AI health discussions (PHI), so without this they linger
  // in memory and could surface to the next account signing in on the same device.
  //
  // ★★★ v242.156.0 · CASE-CHAT-PERSIST-1 — THIS COMMENT USED TO SAY "and are NOT
  // persisted", AND THAT SENTENCE IS NOW FALSE. It is corrected here rather than left,
  // because a comment stating a fact the change falsifies is still code (§1dn(e)), and
  // the next reader would have used it to decide this wipe was the only copy that
  // mattered.
  // ★★ THE WIPE IS UNCHANGED AND STILL NECESSARY, AND THE REASON IS WORTH SAYING: what
  // persistence moved is where the DURABLE copy lives — a row this server hands back
  // only behind patient_may_access_case. It did not make the IN-MEMORY copy safe to
  // leave lying about on a shared device. The two protect against different things.
  // ★ `_hydrated` is cleared with the rest: it is a record of which cases this ACCOUNT
  //   has loaded, and leaving it would let the next account skip a load it never did.
  resetForUser: () => set({ conversations: {}, loadingCaseId: null, error: null, _messageCounts: {}, _hydrated: {} }),

  /**
   * ═══ REHYDRATE ONE CASE'S CONVERSATION FROM THE RECORD ════════════════════════════
   * v242.156.0 · CASE-CHAT-PERSIST-1.
   *
   * ★★ IT APPENDS TO THE OPENER, IT DOES NOT REPLACE THE THREAD. `messages[0]` is the
   * greeting — and on Case Home it is the HERO LINE the screen computed (v242.71.0),
   * which the server never saw and could not have stored. Replacing the array would
   * delete it; stored turns go after it, which is also the order they happened in.
   *
   * ★★★ IT REFUSES TO TOUCH A THREAD THE PATIENT IS ALREADY USING. More than the opener
   * in memory means they have spoken since this screen opened, and their copy is the
   * newer one — the stored turns are a prefix of it and overwriting would delete a
   * message they just watched arrive.
   *
   * ★★ A REAL CASE ONLY. "master", "home" and "profile" are SCREENS, not cases; they
   * have no row, the server never stored them, and asking would 404 on a uuid that is
   * not one. Same predicate the server uses, for the same reason.
   *
   * ★ ONCE PER CASE PER ACCOUNT. `_hydrated` stops a screen that remounts from
   *   re-fetching a transcript it already has, and it is cleared by resetForUser.
   * ★ FAILURE IS SILENT ON SCREEN AND LOUD IN THE CONSOLE. A transcript that cannot be
   *   loaded must not stop the patient talking to the agent.
   */
  hydrateCase: async (caseId) => {
    const isRealCase = !!caseId && caseId.length > 30 && caseId.includes('-')
    if (!isRealCase || get()._hydrated[caseId]) return
    set((state) => ({ _hydrated: { ...state._hydrated, [caseId]: true } }))
    try {
      const res = await aiAPI.caseChatHistory(caseId)
      const stored = Array.isArray(res?.turns) ? res.turns : []
      if (!stored.length) return
      set((state) => {
        const convo = state.conversations[caseId]
        if (!convo || (convo.messages?.length || 0) > 1) return {}
        return {
          conversations: {
            ...state.conversations,
            [caseId]: {
              ...convo,
              messages: [...convo.messages, ...agentMessagesFromStored(stored)],
            },
          },
        }
      })
    } catch (err) {
      console.error('[Agent] history failed', err)
    }
  },

  /** Derived helper — components use this instead of reading loadingCaseId directly */
  isLoading: (caseId) => get().loadingCaseId === caseId,

  /**
   * ═══ SEED OR RESEED ONE CONVERSATION — CALL THIS FROM AN EFFECT ═══════════════════
   * v242.157.0 · AGENT-RENDER-PHASE-SET-1.
   *
   * ★★★ IT USED TO BE CALLED `getConversation` AND IT WAS CALLED FROM A RENDER BODY.
   * A function whose name promises a read and whose body calls set() twice — the
   * reseed delete below, and the greeting seed at the bottom — was reached straight
   * from `OrellaChatAgent`'s component body. React forbids that: a store write during
   * render is a write to a component other components are mid-render against.
   *
   * ★★ D221 REMOVED THE DISAGREEMENT, NOT THE VIOLATION. It stayed survivable only
   * while EXACTLY ONE mounted component did it; the next second opener re-arms the
   * same crash, and nothing stopped a second one being added.
   *
   * ★ THE SPLIT IS THE FIX, AND THE NAMES ARE HALF OF IT: `ensureConversation` says it
   * writes, `getConversation` below is a read and cannot write. A caller can no longer
   * reach a mutation by asking for a value.
   */
  ensureConversation: (caseId, caseContext) => {
    const currentLang = getAgentLang()
    const _screenNow = caseContext?.screen || ''
    let existing = get().conversations[caseId]
    // Reset a still-unsent greeting when the language changes, or — for the
    // single master thread — when the screen changes, so the opening line
    // matches where the patient is. Once they send a message, the thread locks.
    const onlyGreeting = existing && (existing.messages?.length || 0) <= 1
    const langChanged = existing && existing._lang && existing._lang !== currentLang
    const screenChanged = existing && caseId === 'master' && existing._screen !== undefined && existing._screen !== _screenNow
    // v242.74.0 — ★ AND WHEN THE SCREEN'S OWN OPENER CHANGES.
    //
    // On Case Home the opener is not a greeting any more, it IS the hero line
    // the patient reads about their case, and Case Home recomputed it every
    // render: doctor accepts, appointment confirms, consult finalizes, and the
    // sentence followed. Seeding it into a stored thread froze it at whatever
    // the case was when the thread was created, so a patient sitting on the
    // screen while their appointment got confirmed would keep reading "I'll let
    // you know once your appointment is confirmed" indefinitely.
    //
    // Same guard as the other two: only while the thread is still just the
    // opener. Once the patient has said anything, the thread is theirs and
    // rewriting its first line would edit their conversation.
    const openerChanged = existing && caseId !== 'master'
      && typeof caseContext?.opener === 'string' && caseContext.opener.trim()
      && existing._opener !== undefined && existing._opener !== caseContext.opener.trim()
    if (onlyGreeting && (langChanged || screenChanged || openerChanged) && reseedAllowed(caseId)) {
      set((state) => {
        const { [caseId]: _, ...rest } = state.conversations
        return { conversations: rest }
      })
      existing = undefined
    }
    if (!existing) {
      const isMaster = caseId === 'master'
      const screen = _screenNow
      // Initial greeting
      const phase = caseContext?.phase ?? 1
      const status = caseContext?.status || ''
      const isExternal = caseContext?.doctorSource === 'external' || caseContext?.isExternalPath
      const specialty = caseContext?.specialty || ''
      const focusArea = caseContext?.focusArea || ''
      const hasDiagnosis = !!caseContext?.diagnosis
      const doctorName = caseContext?.doctorName || ''
      const profileSummary = caseContext?.profileSummary || {}
      const patientName = caseContext?.firstName || '' // v169.4.0
      // v235.16.0 — temporal signals so the case-scope greeting matches where the
      // case is IN TIME (appointment imminent / confirmed / just-accepted), not
      // only its 4D phase. Mirrors the CaseHome hero card's check-in awareness.
      const checkInEligible = !!caseContext?.checkInEligible
      const appointmentLabel = caseContext?.appointmentLabel || ''

      const _defaultLang = getAgentLang()
      const _defaultG = AGENT_GREETINGS[_defaultLang] || AGENT_GREETINGS.en

      let greeting = _defaultG.welcome(patientName)
      let initialSuggestions = []

      // v242.71.0 (CASEHOME-INLINE-AGENT-1) — `caseContext.opener` lets the
      // SCREEN supply the opening line instead of this store deriving one.
      //
      // Case Home has always computed its own phase-aware narrative for the hero
      // bubble — roughly 200 lines of switch covering encounter 2+, the external
      // doctor path, deliver sub-states and referral notes — while this store
      // derived a SECOND opening line from the same facts for the chat. Two
      // engines, one subject. Now that Case Home renders the agent inline, the
      // bubble and the opener are the same surface, so they must be the same
      // sentence: the screen hands its line down and this store stops guessing.
      //
      // Deliberately case-scope only. The master thread's opener depends on
      // WHICH SCREEN it was opened from, which no single caller can know.
      const openerOverride = (!isMaster && typeof caseContext?.opener === 'string')
        ? caseContext.opener.trim()
        : ''

      // v224.0.0 (2a): master scope → greeting reflects the screen, never a
      // specific case. Case scope → phase-aware case greeting (below).
      if (isMaster) {
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        const _s = AGENT_SUGGESTIONS[_lang] || AGENT_SUGGESTIONS.en
        if (screen === 'profile') {
          const missing = []
          if (!profileSummary.allergiesComplete) missing.push('allergies')
          if (!profileSummary.medicationsComplete) missing.push('medications')
          if (!profileSummary.medicalHistoryComplete) missing.push('medical history')
          if (!profileSummary.familyHistoryComplete) missing.push('family history')
          if (missing.length > 0) {
            greeting = _g.profileIncomplete(missing, patientName)
            initialSuggestions = [_s.helpMeAdd(missing[0]), _s.whyProfileMatters, _s.whatDoctorSees]
          } else {
            greeting = _g.profileComplete(patientName)
            initialSuggestions = [_s.reviewAllergies, _s.updateMedications, _s.whatDoctorSees]
          }
        } else if (screen === 'cases') {
          greeting = _g.casesScreen
        } else if (screen === 'visits') {
          greeting = _g.visitsScreen
        } else if (screen === 'doctors') {
          greeting = _g.doctorsScreen || _g.welcome(patientName)
        } else {
          // home (or any other non-case screen)
          const openedFrom = caseContext?.openedFrom || ''
          greeting = (openedFrom === 'home_search' && _g.triage) ? _g.triage(patientName) : _g.welcome(patientName)
        }
      } else if (status === 'in_follow_up' || (caseContext?.encounterPhase || '').startsWith('follow_up')) {
        // v235.4.0 — follow-up encounter: don't greet as if they're still describing.
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        greeting = (_g.followUpPhase || AGENT_GREETINGS.en.followUpPhase)(patientName)
      } else if (status === 'in_observation') {
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        greeting = (_g.observationPhase || AGENT_GREETINGS.en.observationPhase)(patientName)
      } else if (status === 'completed' || phase === 6) {
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        greeting = hasDiagnosis ? _g.deliverResults(patientName) : _g.deliverConsultComplete
      } else if (status === 'checked_in' || status === 'in_progress') {
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        greeting = doctorName
          ? _g.diagnoseWithDoctor(doctorName)
          : (_g.awaitingReview || AGENT_GREETINGS.en.awaitingReview)(patientName)
      } else if (!isExternal && checkInEligible && (status === 'scheduled' || status === 'claimed')) {
        // v235.16.0 — appointment is imminent (confirmed + within the check-in
        // window) but the patient hasn't checked in yet. The hero card already
        // nudges "tap Diagnose to check in"; open the chat in the same station.
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        greeting = (_g.appointmentImminent || AGENT_GREETINGS.en.appointmentImminent)(patientName)
      } else if (!isExternal && status === 'scheduled') {
        // v235.16.0 — appointment confirmed (not yet imminent). Name the doctor /
        // date when we have them and invite pre-visit prep.
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        greeting = (_g.appointmentConfirmed || AGENT_GREETINGS.en.appointmentConfirmed)(doctorName, appointmentLabel, patientName)
      } else if (!isExternal && status === 'claimed') {
        // v235.16.0 — doctor accepted the case but the appointment isn't confirmed
        // yet. Previously phase 4 fell through to "ready to share" (stale).
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        greeting = doctorName
          ? _g.diagnoseWithDoctor(doctorName)
          : (_g.caseReviewing || AGENT_GREETINGS.en.caseReviewing)(patientName)
      } else if (phase <= 2) {
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        const topic = focusArea || specialty || (_lang === 'es' ? 'sus síntomas' : 'your symptoms')
        greeting = _g.describePhase(topic, patientName)
      } else if (phase === 3) {
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        greeting = _g.detectPhase(patientName)
      } else if (phase === 4) {
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        if (isExternal) {
          greeting = _g.shareExternal
        } else {
          greeting = _g.shareOrella
        }
      } else if (phase === 5) {
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        if (isExternal) {
          greeting = _g.diagnoseExternal
        } else if (doctorName) {
          greeting = _g.diagnoseWithDoctor(doctorName)
        } else {
          greeting = _g.diagnoseWaiting
        }
      } else if (phase === 6) {
        const _lang = getAgentLang()
        const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
        if (hasDiagnosis) {
          greeting = _g.deliverResults(patientName)
        } else {
          greeting = _g.deliverConsultComplete
        }
      }

      set((state) => ({
        conversations: {
          ...state.conversations,
          [caseId]: {
            _lang: currentLang,
            _screen: _screenNow,
            _opener: openerOverride,
            messages: [{
              role: 'assistant',
              content: openerOverride || greeting,
              timestamp: Date.now(),
              _isOpener: true, // s120: pinned first in the thread (orderThread)
            }],
            suggestions: initialSuggestions,
            suggestionsKind: initialSuggestions.length > 0 ? 'follow_ups' : null,
            pendingActions: []
          }
        }
      }))
    }
    return get().conversations[caseId]
  },

  /**
   * A READ. No set(), ever — that is the whole point of the split above, and
   * `scripts/check-render-phase-writes.mjs` is the arm that keeps it true.
   */
  getConversation: (caseId) => get().conversations[caseId],

  _checkRateLimit: (caseId) => {
    const counts = get()._messageCounts
    const entry = counts[caseId]
    const now = Date.now()
    if (!entry || now > entry.resetAt) {
      set((state) => ({
        _messageCounts: { ...state._messageCounts, [caseId]: { count: 0, resetAt: now + 3600000 } }
      }))
      return true
    }
    return entry.count < 20
  },

  sendMessage: async (caseId, message, caseContext = {}) => {
    const { _checkRateLimit } = get()

    if (!_checkRateLimit(caseId)) {
      const _lang = getAgentLang()
      const _g = AGENT_GREETINGS[_lang] || AGENT_GREETINGS.en
      const limitMsg = {
        role: 'assistant',
        content: _g.rateLimit,
        timestamp: Date.now()
      }
      set((state) => ({
        conversations: {
          ...state.conversations,
          [caseId]: {
            ...state.conversations[caseId],
            messages: [...(state.conversations[caseId]?.messages || []), limitMsg],
            suggestions: [], suggestionsKind: null, pendingActions: state.conversations[caseId]?.pendingActions || []
          }
        }
      }))
      return
    }

    const userMsg = { role: 'user', content: message, timestamp: Date.now() }
    const currentConvo = get().conversations[caseId] || { messages: [], suggestions: [], pendingActions: [] }

    set((state) => ({
      loadingCaseId: caseId, error: null,
      conversations: {
        ...state.conversations,
        [caseId]: { ...currentConvo, messages: [...currentConvo.messages, userMsg], suggestions: [], suggestionsKind: null }
      }
    }))

    set((state) => ({
      _messageCounts: {
        ...state._messageCounts,
        [caseId]: { ...state._messageCounts[caseId], count: (state._messageCounts[caseId]?.count || 0) + 1 }
      }
    }))

    // v234.4 — Sanitize history before it leaves the client. The model API only
    // accepts user/assistant roles; doctor bubbles carry role 'md_message' and
    // system notices carry 'system'. Shipping those as-is errored the call (or
    // dropped the doctor's words, so the agent "lost track"). Keep user/assistant
    // and fold each MD message into a clearly-attributed assistant turn so the
    // agent stays grounded in what the physician actually told the patient.
    const historyForAPI = get().conversations[caseId].messages
      .filter(m => m.content && (m.role === 'user' || m.role === 'assistant' || m.role === 'md_message'))
      .slice(-10)
      .map((m) => {
        if (m.role === 'md_message') {
          const raw = (m.mdName || '').replace(/^dr\.?\s*/i, '').trim()
          const who = raw ? clinicianName(clinicianFromText(raw), 'display', 'en') : 'the physician'
          return { role: 'assistant', content: `(${who} sent the patient this message in the case: "${m.content}")` }
        }
        return { role: m.role, content: m.content }
      })

    try {
      const response = await aiAPI.agentChat(message, historyForAPI, caseContext, caseId)

      // v234.4 — agentChat swallows network/API failures into { error: true }.
      // Treat that as a real send failure: record it (reason-aware) and do NOT
      // append a fake assistant answer.
      if (response?.error) {
        set((state) => ({
          loadingCaseId: null,
          conversations: {
            ...state.conversations,
            [caseId]: {
              ...state.conversations[caseId],
              failedSend: { kind: _classifyAgentStatus(response.errorStatus), text: message, context: caseContext },
            }
          }
        }))
        return
      }

      const assistantMsg = {
        // PROD-1157: a stable identity, so a prep card names its message, not a position.
        id: newAgentMessageId(),
        role: 'assistant',
        content: response.message,
        timestamp: Date.now(),
        isEmergency: response.isEmergency || false,
        profileActions: response.profileActions || [],
        navigation: response.navigation || null,
        messengerPrep: response.messengerPrep || null, // v220.1
        sources: response.sources || [], // v242.47.0: MedlinePlus citations (1.4.1)
      }

      set((state) => ({
        loadingCaseId: null,
        conversations: {
          ...state.conversations,
          [caseId]: {
            ...state.conversations[caseId],
            failedSend: null,
            messages: [...state.conversations[caseId].messages, assistantMsg],
            suggestions: response.suggestions || [],
            suggestionsKind: response.suggestionsKind || null, // v206.9: 'answer_options' | 'follow_ups' | null
            pendingActions: [
              ...(state.conversations[caseId].pendingActions || []),
              ...(response.profileActions || []).map((a, i) => ({
                ...a, id: `${Date.now()}-${i}`, status: 'pending'
              }))
            ]
          }
        }
      }))
    } catch (err) {
      console.error('[AgentStore] Send error:', err)
      // v234.4 — record a reason-aware failure on the patient's own message
      // instead of faking an assistant reply.
      set((state) => ({
        loadingCaseId: null, error: err?.error || err?.message,
        conversations: {
          ...state.conversations,
          [caseId]: {
            ...state.conversations[caseId],
            failedSend: { kind: _classifyAgentStatus(err?.status), text: message, context: caseContext },
          }
        }
      }))
    }
  },

  // v234.4 — Re-send the last message that failed: drop the unsent bubble,
  // clear the failure, and run the normal send again.
  retrySend: async (caseId) => {
    const convo = get().conversations[caseId]
    const fs = convo?.failedSend
    if (!fs) return
    set((state) => {
      const msgs = [...(state.conversations[caseId]?.messages || [])]
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'user' && msgs[i].content === fs.text) { msgs.splice(i, 1); break }
      }
      return {
        conversations: {
          ...state.conversations,
          [caseId]: { ...state.conversations[caseId], messages: msgs, failedSend: null },
        }
      }
    })
    return get().sendMessage(caseId, fs.text, fs.context || {})
  },

  acceptAction: (caseId, actionId) => {
    let actionData = null
    set((state) => {
      const convo = state.conversations[caseId]
      if (!convo) return state
      const updated = (convo.pendingActions || []).map(a => {
        if (a.id === actionId && a.status === 'pending') {
          actionData = a
          return { ...a, status: 'accepted' }
        }
        return a
      })
      return { conversations: { ...state.conversations, [caseId]: { ...convo, pendingActions: updated } } }
    })
    return actionData
  },

  dismissAction: (caseId, actionId) => {
    set((state) => {
      const convo = state.conversations[caseId]
      if (!convo) return state
      const updated = (convo.pendingActions || []).map(a =>
        a.id === actionId ? { ...a, status: 'dismissed' } : a
      )
      return { conversations: { ...state.conversations, [caseId]: { ...convo, pendingActions: updated } } }
    })
  },

  /** v220.1 — Patient confirms an agent-prepped escalation packet.
   *  Sends to the API, then injects a system 'sent' pill and clears the prep card.
   *  Returns { ok, threadId } | { ok: false, error, status } | { ok: false, error, kind: 'client' }.
   *  ★ PROD-1157: `msgRef` is the card's message IDENTITY (agentPrepRef.prepMsgRef: its id, else
   *    its index in the FULL list), never a position in a folded list. A refusal made here,
   *    before any request, says kind 'client' — it must never read as "on our end".
   */
  sendMessengerPrep: async (caseId, msgRef, overrideBody) => {
    const state = get()
    const convo = state.conversations[caseId]
    if (!convo) return { ok: false, error: 'no_conversation', kind: 'client' }
    const at = resolvePrepIndex(convo.messages, msgRef)
    const msg = at >= 0 ? convo.messages[at] : null
    if (!msg?.messengerPrep) return { ok: false, error: 'no_prep', kind: 'client' }

    const prep = msg.messengerPrep
    // v226.9 — honor an edited body if the patient changed it in the card.
    const body = (typeof overrideBody === 'string' && overrideBody.trim())
      ? overrideBody.trim()
      : prep.distilled_question
    const isReply = prep.mode === 'reply' && prep.thread_id

    try {
      let result
      let threadId
      if (isReply) {
        // Reply into the physician's existing thread (not a new escalation).
        result = await messengerAPI.reply(caseId, prep.thread_id, body)
        threadId = prep.thread_id
      } else {
        const payload = {
          body,
          distilled_question: body,
          verbatim_excerpt: prep.verbatim_excerpt,
          prep_context: prep.prep_context,
          category: prep.category,
        }
        result = await messengerAPI.send(caseId, payload)
        threadId = result.thread_id
      }
      set((s) => {
        const c = s.conversations[caseId]
        if (!c) return s
        // Resolved again: the list may have moved during the request (loadCaseMessages sorts).
        const sentAt = resolvePrepIndex(c.messages, msgRef)
        const newMsgs = c.messages.map((m, i) =>
          i === sentAt ? { ...m, messengerPrep: null, _prepSent: true } : m
        )
        const now = new Date()
        const timeStr = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        // ★★★ v242.157.0 — PATIENT-OWN-MESSAGE-INVISIBLE-1, the third copy.
        //   The prep card is replaced on send and the confirmation line below
        //   says only "Sent to your doctor · <time>" — IT DOES NOT CONTAIN THE
        //   BODY. So the card, the confirmation and the reload each lost the
        //   text independently and the patient had no way to see what they had
        //   asked. Their own words go on screen here, carrying the server's
        //   `message_id` so the reload above recognises this bubble as the same
        //   row and does not add a second copy of it.
        newMsgs.push({
          role: BUBBLE_ROLE_PATIENT,
          content: body,
          timestamp: Date.now(),
          _serverId: result?.message_id || null,
          threadId,
        })
        newMsgs.push({
          role: 'system',
          systemKind: isReply ? 'messenger_replied' : 'messenger_sent',
          content: isReply ? `Replied to your doctor · ${timeStr}` : `Sent to your doctor · ${timeStr}`,
          timestamp: Date.now(),
          threadId,
        })
        return {
          conversations: {
            ...s.conversations,
            [caseId]: { ...c, messages: newMsgs, lastSentThreadId: threadId },
          },
        }
      })
      return { ok: true, threadId }
    } catch (err) {
      console.error('[AgentStore] sendMessengerPrep failed', err)
      return { ok: false, error: err?.error || err?.message || 'send_failed', status: err?.status ?? null }
    }
  },

  /** v220.1 — Load case_messages from DB and merge MD-role messages into
   *  the conversation as 'md_message' bubbles. Idempotent; safe to call
   *  on every chat open.
   */
  loadCaseMessages: async (caseId) => {
    if (!caseId || typeof caseId !== 'string') return
    // Skip non-UUID case ids: 'home', 'profile', 'home-chat', 'doctors-finder', 'global'
    if (caseId.length < 30 || !caseId.includes('-')) return
    try {
      // P17 step 3 (TEST 1.38 side note): the tail, as every other id in a store log; the head
      //   of a UUID is the part two ids are least likely to differ in.
      console.log('[loadCaseMessages] v242.157 fetching for case', caseId?.slice(-8))
      const data = await messengerAPI.getThread(caseId)
      const remoteMessages = data.messages || []
      // ★★★ v242.157.0 — PATIENT-OWN-MESSAGE-INVISIBLE-1. THIS LINE USED TO READ
      //   `remoteMessages.filter(m => m.role === 'md')`, so the patient's own
      //   message was fetched from the server and thrown away on arrival, and
      //   the early return below meant that when theirs was the only message in
      //   the thread NOTHING merged at all. It was never a storage failure —
      //   the server had it the whole time. `utils/caseThread` owns the
      //   direction question now; nothing here re-derives it.
      const remoteThread = threadMessages(remoteMessages)
      console.log(`[loadCaseMessages] v242.157 got ${remoteMessages.length} total, ${remoteThread.length} in-thread (both directions)`)
      if (remoteThread.length === 0) return

      set((s) => {
        const c = s.conversations[caseId]
        // v220.8 — If the conversation isn't initialized yet (race with getConversation),
        // create a minimal shell so we don't silently drop the messages.
        const baseConvo = c || { messages: [], suggestions: [], suggestionsKind: null, pendingActions: [] }

        // ★★ DEDUPE ON THE SERVER ID IN EITHER DIRECTION. The old set collected
        //   ids from `md_message` bubbles only; now that the patient's own
        //   bubbles carry a `_serverId` too, a one-role dedupe would re-add
        //   theirs on every chat open.
        const existingIds = seenServerIds(baseConvo.messages)

        const toAdd = remoteThread
          .filter(m => !existingIds.has(m.id))
          .map(threadBubble)

        console.log(`[loadCaseMessages] v242.157 ${toAdd.length} new bubble(s) to merge (had ${existingIds.size} already)`)
        // s120 (Dr. Rami, build 89): the opener "should be the first dialogue of the chat everything else opens
        //   underneath". It is stamped when the thread is seeded, so a physician message written earlier sorted
        //   ABOVE it. orderThread pins it first (and repairs a stored thread already in the wrong order).
        const allMsgs = orderThread([...baseConvo.messages, ...toAdd], baseConvo._opener)
        if (toAdd.length === 0 && allMsgs.every((m, i) => m === baseConvo.messages[i])) return s
        return {
          conversations: {
            ...s.conversations,
            [caseId]: { ...baseConvo, messages: allMsgs },
          },
        }
      })
    } catch (err) {
      console.error('[loadCaseMessages] v220.8 failed', err)
    }
  },

  /** v181.0.0: Mark the last assistant message as streamed so it doesn't re-stream on reopen */
  markStreamed: (caseId) => {
    set((state) => {
      const convo = state.conversations[caseId]
      if (!convo || !convo.messages?.length) return state
      const msgs = [...convo.messages]
      // Find the last assistant message and mark it
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant' && !msgs[i]._streamed) {
          msgs[i] = { ...msgs[i], _streamed: true }
          break
        }
      }
      return { conversations: { ...state.conversations, [caseId]: { ...convo, messages: msgs } } }
    })
  },

  clearConversation: (caseId) => {
    set((state) => {
      const { [caseId]: _, ...rest } = state.conversations
      return { conversations: rest }
    })
  }
}))

export default useAgentStore
